import React, {useState, useRef, useEffect, useMemo} from 'react';
import {Typography, ClickAwayListener} from '@mui/material';
import {XCloseIcon, ChevronDownFrontdeskIcon} from '$assets/svgs';
import DatePicker from '$components/DatePicker';
import FrontdeskButton from '$components/FrontdeskButton';
import DialogComponent from '$components/DialogComponent';
import useTimezone from '~/hooks/useTimezone';
import {useDownloadReport} from '~/modules/frontdesk/hooks';
import './styles.scss';

interface DataOption {
  id: string;
  label: string;
}

const dataOptions: DataOption[] = [
  {id: 'monthly-revenue', label: 'Monthly Revenue'},
  {id: 'outstanding-insurance', label: 'Outstanding Insurance Amount'},
  {id: 'insurance-claims', label: 'Insurance Claims Management'},
  {id: 'payment-methods', label: 'Preferred Payment Methods'},
  {id: 'top-insurance', label: 'Top Insurance Companies'},
  {id: 'outstanding-payment', label: 'Outstanding Payments'},
];

const dataOptionMapping: Record<string, string> = {
  'monthly-revenue': 'monthly_revenue',
  'outstanding-insurance': 'outstanding_insurance_amounts',
  'insurance-claims': 'insurance_claims_management',
  'payment-methods': 'preferred_payment_methods',
  'top-insurance': 'top_insurance_companies',
  'outstanding-payment': 'outstanding_payments',
};

interface DownloadReportModalProps {
  open: boolean;
  onClose: () => void;
}

const DownloadReportModal: React.FC<DownloadReportModalProps> = ({
  open,
  onClose,
}) => {
  const timezone = useTimezone();

  const initialDates = useMemo(() => {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);
    return {
      dateFrom: oneMonthAgo,
      dateTo: today,
    };
  }, []);

  const [dateFrom, setDateFrom] = useState<Date | null>(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState<Date | null>(initialDates.dateTo);
  const [selectedDataOptions, setSelectedDataOptions] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setDateFrom(initialDates.dateFrom);
      setDateTo(initialDates.dateTo);
      setSelectedDataOptions([]);
      setDropdownOpen(false);
    }
  }, [open, initialDates.dateFrom, initialDates.dateTo]);

  const {
    isDownloadingCSV,
    isDownloadingPDF,
    handleGenerateCSV,
    handleGeneratePDF,
  } = useDownloadReport({
    dateFrom,
    dateTo,
    selectedDataOptions,
    dataOptionMapping,
  });

  const handleDataOptionToggle = (optionId: string) => {
    setSelectedDataOptions(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );

    setDropdownOpen(false);
  };

  const handleRemoveOption = (optionId: string) => {
    setSelectedDataOptions(prev => prev.filter(id => id !== optionId));
  };

  const availableOptions = dataOptions.filter(
    option => !selectedDataOptions.includes(option.id)
  );

  const handleClickAway = () => {
    setDropdownOpen(false);
  };

  const hasSelectedOptions = selectedDataOptions.length > 0;
  const isCSVButtonDisabled = !hasSelectedOptions || isDownloadingCSV;
  const isPDFButtonDisabled = !hasSelectedOptions || isDownloadingPDF;

  const modalContent = (
    <div className="download-report-modal-content">
      <div className="download-report-title-section">
        <Typography variant="h6" className="download-report-title">
          Download report
        </Typography>
      </div>
      <div className="date-range-section">
        <div className="date-inputs">
          <div className="date-input">
            <Typography variant="body2" className="date-label">
              Data from
            </Typography>
            <DatePicker
              value={dateFrom}
              onChange={setDateFrom}
              timezone={timezone}
              placeholder="Select a date"
              disableFuture={true}
            />
          </div>
          <div className="date-input">
            <Typography variant="body2" className="date-label">
              To
            </Typography>
            <DatePicker
              value={dateTo}
              onChange={setDateTo}
              timezone={timezone}
              placeholder="Select a date"
              disableFuture={true}
            />
          </div>
        </div>
      </div>

      <div className="data-inclusion-section">
        <Typography variant="subtitle1" className="section-title">
          Select data to include *
        </Typography>

        <ClickAwayListener onClickAway={handleClickAway}>
          <div className="custom-dropdown" ref={dropdownRef}>
            <div
              className="dropdown-trigger"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <Typography variant="body2" className="dropdown-placeholder">
                Choose one or more options
              </Typography>
              <ChevronDownFrontdeskIcon
                className={`dropdown-icon ${dropdownOpen ? 'open' : ''}`}
              />
            </div>

            {dropdownOpen && (
              <div className="dropdown-menu">
                {availableOptions.map(option => (
                  <div
                    key={option.id}
                    className="dropdown-option"
                    onClick={() => handleDataOptionToggle(option.id)}
                  >
                    {option.label}
                  </div>
                ))}
                {availableOptions.length === 0 && (
                  <div className="dropdown-option disabled">
                    All options selected
                  </div>
                )}
              </div>
            )}
          </div>
        </ClickAwayListener>

        {selectedDataOptions.length > 0 && (
          <div className="selected-options">
            {selectedDataOptions.map(optionId => {
              const option = dataOptions.find(opt => opt.id === optionId);
              return (
                <FrontdeskButton
                  key={optionId}
                  title={option?.label || ''}
                  variant="primary"
                  onClick={() => handleRemoveOption(optionId)}
                  startIcon={<XCloseIcon />}
                />
              );
            })}
          </div>
        )}
      </div>

      <div className="report-generation-section">
        <div className="report-options">
          <div className="report-option">
            <Typography variant="body1" className="report-type">
              CSV report
            </Typography>
            <FrontdeskButton
              title={isDownloadingCSV ? 'Downloading CSV...' : 'Download'}
              variant="primary"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleGenerateCSV();
              }}
              className={`generate-button ${!hasSelectedOptions ? 'no-options-selected' : ''}`}
              disabled={isCSVButtonDisabled}
            />
          </div>

          <div className="report-option">
            <Typography variant="body1" className="report-type">
              PDF report
            </Typography>
            <FrontdeskButton
              title={isDownloadingPDF ? 'Downloading PDF...' : 'Download'}
              variant="primary"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleGeneratePDF();
              }}
              className={`generate-button ${!hasSelectedOptions ? 'no-options-selected' : ''}`}
              disabled={isPDFButtonDisabled}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DialogComponent
      open={open}
      handleClose={() => onClose()}
      dialogTitle="Download report"
      dialogBody={modalContent}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '600px',
          width: '100%',
        },
      }}
    />
  );
};

export default DownloadReportModal;
